// Block D: Edit tags (Business). Add a tag, recolour any tag, remove a custom one.
//
// NOTHING LOOKS SAVED UNTIL IT IS. The old tag manager updated the list and fired the save with
// `.catch(() => {})`, so a tag that never reached the server looked kept and was gone on the next
// load. Here every change waits for the server, and a failure is said beside the control, with the
// list as it really is.
//
// Removing a tag says how many contacts carry it, then takes it off each of them through the normal
// contact save (PUT /api/contacts/:id). The contacts themselves are never deleted. If some of those
// saves fail, it says how many, and the tag stays in the list so nothing is left half-explained.
import React, { useState } from 'react';
import { Modal, Button, Input, ConfirmDialog, ErrorState } from '../../ui';
import { v } from '../../ui/tokens';
import { PRESET_TAGS, TAG_PALETTE, tagColor } from './contactTags';
import './Contacts.css';

const NAMES = {
  'pastel-green': 'green', 'pastel-blue': 'blue', 'pastel-yellow': 'yellow', 'pastel-orange': 'orange', 'pastel-pink': 'pink',
  'pastel-lilac': 'lilac', 'pastel-periwinkle': 'periwinkle', 'pastel-coral': 'coral', 'pastel-mint': 'mint', 'pastel-neutral': 'grey',
};

export default function TagEditor({
  open, onClose, tagsInUse, customTags, tagColors, prefsError, onRetryPrefs,
  tagCounts, onAddTag, onRecolour, onRemoveTag, canRecolour = true,
}) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState({});      // { add?: string, [tag]: string }
  const [confirm, setConfirm] = useState(null);

  const all = [...new Set([...PRESET_TAGS, ...customTags, ...tagsInUse])];
  const removable = (t) => !PRESET_TAGS.includes(t);

  async function add() {
    const t = name.trim();
    if (!t) return;
    if (all.some((x) => x.toLowerCase() === t.toLowerCase())) { setError({ add: `"${t}" is already a tag.` }); return; }
    setBusy(true); setError({});
    try { await onAddTag(t); setName(''); } catch (e) { setError({ add: e.message }); } finally { setBusy(false); }
  }

  async function recolour(tag, color) {
    setError((x) => ({ ...x, [tag]: '' }));
    try { await onRecolour(tag, color); } catch (e) { setError((x) => ({ ...x, [tag]: e.message })); }
  }

  return (
    <Modal open={open} onClose={onClose} title="Edit tags" color="pastel-green" width={560}
      footer={<Button variant="confirm" onClick={onClose}>Done</Button>}>
      {prefsError ? (
        <ErrorState title="Your tags didn't load" message={`${prefsError} Adding, recolouring and removing tags is off until they load.`} onRetry={onRetryPrefs} />
      ) : (
        <>
          <div className="contacts-form__add-tag">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="New tag name" aria-label="New tag name"
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
            <Button variant="create" size="sm" onClick={add} disabled={busy || !name.trim()}>{busy ? 'Adding…' : 'Add tag'}</Button>
          </div>
          {error.add && <p className="contacts-error" role="alert">{error.add}</p>}

          <ul className="contacts-tagedit">
            {all.map((t) => (
              <li key={t} className="contacts-tagedit__row">
                <span className="contacts-tagedit__name">
                  <span className="contacts-dot" style={{ '--ui-bg': v(tagColor(t, tagColors)) }} aria-hidden="true" />
                  {t}
                  <span className="contacts-muted"> · {tagCounts[t] || 0}</span>
                </span>
                {canRecolour && (
                  <span className="contacts-swatches" role="group" aria-label={`Colour for ${t}`}>
                    {TAG_PALETTE.map((c) => (
                      <button key={c} type="button" className="contacts-swatch ui-hit" style={{ '--ui-bg': v(c) }}
                        aria-pressed={tagColor(t, tagColors) === c} aria-label={NAMES[c]} title={NAMES[c]}
                        onClick={() => recolour(t, c)} />
                    ))}
                  </span>
                )}
                {removable(t) && (
                  <Button variant="destructive" size="sm" onClick={() => setConfirm(t)}>Remove</Button>
                )}
                {error[t] && <p className="contacts-error contacts-tagedit__error" role="alert">{error[t]}</p>}
              </li>
            ))}
          </ul>
          <p className="contacts-help">{canRecolour
            ? 'Tag colours are the same for everyone in the business. The preset tags (Client, Press, Team and the rest) can be recoloured but not removed.'
            : 'Tag colours are the same for everyone in the business; the owner or an admin sets them.'}</p>
        </>
      )}

      <ConfirmDialog
        open={confirm !== null}
        onClose={() => setConfirm(null)}
        title={`Remove the tag "${confirm || ''}"?`}
        confirmLabel="Remove tag"
        message={(() => {
          const n = tagCounts[confirm] || 0;
          return n
            ? `${n} contact${n === 1 ? ' has' : 's have'} this tag. Removing it takes it off ${n === 1 ? 'that contact' : `all ${n}`}; the contacts themselves are kept.`
            : 'No contacts have this tag. Removing it takes it off the list.';
        })()}
        onConfirm={async () => {
          const t = confirm;
          // ConfirmDialog shows a thrown message inside itself, and stays open.
          await onRemoveTag(t);
        }}
      />
    </Modal>
  );
}
